import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { HiMenuAlt3 } from 'react-icons/hi';
import { FaTimes, FaInstagram, FaTiktok, FaYoutube } from 'react-icons/fa';
import { SignedIn, SignedOut } from '@clerk/clerk-react';
import { CLERK_ENABLED } from '../lib/environment';
import { UserButtonConfig } from './UserButtonConfig';
import { TOOLS } from '../lib/tools';

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsRef = useRef(null);
  const toolsToggleRef = useRef(null);
  const mobileToggleRef = useRef(null);
  const location = useLocation();
  const isToolsRoute = location.pathname.startsWith('/tools');

  useEffect(() => {
    setMobileOpen(false);
    setToolsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onMouseDown = (event) => {
      if (toolsRef.current && !toolsRef.current.contains(event.target)) {
        setToolsOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        if (toolsOpen && toolsRef.current?.contains(document.activeElement)) {
          toolsToggleRef.current?.focus();
        } else if (mobileOpen && document.activeElement?.closest('.ff-header__nav-wrap')) {
          mobileToggleRef.current?.focus();
        }
        setToolsOpen(false);
        if (!toolsOpen) setMobileOpen(false);
      }
    };

    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileOpen, toolsOpen]);

  const onToolsBlur = (event) => {
    if (toolsRef.current && !toolsRef.current.contains(event.relatedTarget)) {
      setToolsOpen(false);
    }
  };

  const navItems = useMemo(
    () => [
      { to: '/', label: 'Home', exact: true },
      { to: '/cap', label: 'Cap' },
      { to: '/posts', label: 'Posts' },
    ],
    []
  );

  return (
    <header className="ff-header">
      <div className="ff-header__inner">
        <div className="ff-header__bar" />
        <div className="ff-header__row">
          <Link to="/" className="ff-brand" aria-label="FlightFormula home">
            <img src="/LogoV2.png" alt="FlightFormula" width="214" height="34" />
          </Link>

          <button
            ref={mobileToggleRef}
            type="button"
            className="ff-menu-toggle"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            aria-controls="ff-main-navigation"
            onClick={() => { setMobileOpen((v) => !v); setToolsOpen(false); }}
          >
            {mobileOpen ? <FaTimes /> : <HiMenuAlt3 />}
          </button>

          <div id="ff-main-navigation" className="ff-header__nav-wrap" data-open={mobileOpen ? 'true' : 'false'}>
            <div className="ff-header__content">
              <nav className="ff-nav" aria-label="Main navigation">
                {navItems.map((item) => (
                  <NavLink key={item.to} to={item.to} end={item.exact} className="ff-nav-link">
                    {item.label}
                  </NavLink>
                ))}

                <div ref={toolsRef} className="ff-nav-tools" onBlur={onToolsBlur}>
                  <button
                    ref={toolsToggleRef}
                    type="button"
                    className="ff-nav-button"
                    data-active={toolsOpen || isToolsRoute ? 'true' : 'false'}
                    onClick={() => setToolsOpen((v) => !v)}
                    aria-expanded={toolsOpen}
                    aria-controls="ff-tools-menu"
                  >
                    Tools <span aria-hidden="true">▾</span>
                  </button>
                  {toolsOpen && (
                    <div id="ff-tools-menu" className="ff-tools-menu ff-animate-rise">
                      {TOOLS.map((tool) => (
                        <Link key={tool.to} to={tool.to} className="ff-tools-menu__item">
                          <span>
                            <div className="ff-tools-menu__title">{tool.title}</div>
                            <div className="ff-tools-menu__copy">{tool.description}</div>
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <NavLink to="/contact" className="ff-nav-link">
                  Contact
                </NavLink>
              </nav>

              <div className="ff-header__right">
                <div className="ff-socials" aria-label="Social links">
                  <a href="https://www.instagram.com/flightformula/" target="_blank" rel="noreferrer" aria-label="Instagram">
                    <FaInstagram />
                  </a>
                  <a href="https://www.tiktok.com/@flightformula" target="_blank" rel="noreferrer" aria-label="TikTok">
                    <FaTiktok />
                  </a>
                  <a href="https://www.youtube.com/@FlightFormula" target="_blank" rel="noreferrer" aria-label="YouTube">
                    <FaYoutube />
                  </a>
                </div>

                {CLERK_ENABLED ? (
                  <>
                    <SignedIn>
                      <div className="ff-header-user">
                        <UserButtonConfig />
                      </div>
                    </SignedIn>
                    <SignedOut>
                      <Link to="/signin" className="ff-nav-link ff-header-login-btn">
                        Sign in
                      </Link>
                    </SignedOut>
                  </>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
